import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest } from "../../../core/common/api";
import { canManageUsers } from "../../../core/common/adminPermissions";
import PaginationBar from "../../../shared/PaginationBar";
import ResetButton from "../../../shared/ResetButton";
import useTableSearch from "../../../shared/useTableSearch";
import { clubLabel } from "../../../shared/Utils";

const AllSaleUser = () => {
  const navigate = useNavigate();
  const latestRequest = useRef(0);
  const [users, setUsers] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    totalPages: 1,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    club: "all",
    source: "all",
    rank: "all",
  });
  const searchTerm = useTableSearch(filters.search);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUsers = useCallback(
    async (page = 1) => {
      const requestId = ++latestRequest.current;
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          search: searchTerm,
          page: String(page),
          limit: String(pagination.limit),
        });
        if (filters.club !== "all") params.append("club", filters.club);
        if (filters.source !== "all") params.append("source", filters.source);
        if (filters.rank !== "all") params.append("rank", filters.rank);
        const res = await apiRequest(`/sale-users?${params.toString()}`, "GET");
        if (requestId !== latestRequest.current) return;
        if (res.success) {
          setUsers(res.users || []);
          setPagination({
            page: res.pagination?.page || page,
            limit: res.pagination?.limit || pagination.limit,
            totalPages: res.pagination?.totalPages || 1,
            total: res.pagination?.total ?? res.totalUsers ?? 0,
          });
        }
      } catch (error: any) {
        if (requestId !== latestRequest.current) return;
        setUsers([]);
        Swal.fire(
          "Error Loading Sale Users",
          error.message || "Failed to load sale user list",
          "error",
        );
      } finally {
        if (requestId === latestRequest.current) setIsLoading(false);
      }
    },
    [filters.club, filters.source, filters.rank, searchTerm, pagination.limit],
  );

  useEffect(() => {
    fetchUsers(1);
  }, [fetchUsers]);

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">User Management</div>
          <h1 className="page-title">
            Sale <em>Users</em>
          </h1>
          <div className="page-sub">
            Placement-tree members created from sales or by admin. These IDs cannot
            log in — income credits the linked registered buyer.
          </div>
        </div>
        <div className="page-hdr-right">
          {canManageUsers() && (
            <Link to="/add-sale-users" className="btn-sm btn-primary">
              Add Sale User
            </Link>
          )}
        </div>
      </div>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search by name, member ID, buyer, sponsor..."
          />
        </div>
        <select
          className="filter-select"
          aria-label="Filter by club"
          value={filters.club}
          onChange={(e) => setFilters({ ...filters, club: e.target.value })}
        >
          <option value="all">All clubs</option>
          {[1, 2, 3, 4, 5, 6].map((club) => (
            <option key={club} value={String(club)}>
              Club {club}
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          aria-label="Source"
          value={filters.source}
          onChange={(e) => setFilters({ ...filters, source: e.target.value })}
        >
          <option value="all">All sources</option>
          <option value="sale">Sale</option>
          <option value="admin">Admin</option>
        </select>
        <select
          className="filter-select"
          aria-label="Rank"
          value={filters.rank}
          onChange={(e) => setFilters({ ...filters, rank: e.target.value })}
        >
          <option value="all">All ranks</option>
          <option value="none">No rank</option>
          {["Leader", "Director", "Partner", "Royalty"].map((rank) => (
            <option key={rank} value={rank}>
              {rank}
            </option>
          ))}
        </select>
        <ResetButton
          onClick={() =>
            setFilters({ search: "", club: "all", source: "all", rank: "all" })
          }
        />
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Sale Users ({pagination.total})</h2>
          <p>Network placement accounts stored in saleusermodel</p>
        </div>
        <div className="table-scroll">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Member ID</th>
                <th>Buyer ID</th>
                <th>Sponsor</th>
                <th>Placement</th>
                <th>Club</th>
                <th>Rank</th>
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="empty-state">
                    Loading Sale Users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty-state">
                    No sale users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <strong>{user.name || "—"}</strong>
                    </td>
                    <td>
                      <strong>{user.memberId || "—"}</strong>
                    </td>
                    <td>{user.buyerId || "—"}</td>
                    <td>{user.sponsorId || "—"}</td>
                    <td>
                      {user.placementId
                        ? `${user.placementId} · #${user.position || "—"}`
                        : "Company root"}
                    </td>
                    <td>{clubLabel(user.club)}</td>
                    <td>{user.buyerRank || "No rank"}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          user.source === "admin" ? "success" : "warning"
                        }`}
                      >
                        {user.source === "admin" ? "Admin" : "Sale"}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => navigate(`/sale-users/${user._id}`)}
                        title="View Sale User"
                        className="action-button edit"
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="sale users"
          pageSizeOptions={[100, 200, 500, 1000]}
          onPageChange={fetchUsers}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, page: 1, limit }))
          }
        />
      </section>
    </>
  );
};

export default AllSaleUser;
